/**
 * Client-side Tradera access.
 *
 * Search goes through the Cloudflare Worker proxy (same worker as the training
 * data): the Tradera app key lives in Cloudflare Secrets and never reaches the
 * browser. The proxy runs only when the page calls it — nothing is scheduled.
 *
 * Watches + "already seen" ids are stored on the user's own Firestore doc
 * (users/{uid}.traderaJson), the same per-account pattern as settingsJson.
 *
 * Read-only: search and alerts only. No bidding, buying or messaging.
 */
import firebase from "firebase/app";
import "firebase/auth";
import { db } from "./firebase";

const PROXY =
  process.env.REACT_APP_PROXY_URL
    ?? "https://intervals-proxy.sebastiangranlund.workers.dev";

/** A single listing, normalized from the real /v4/search response shape. */
export interface TraderaItem {
  id: number;
  shortDescription: string;
  itemUrl: string;
  thumbnailLink: string | null;
  endDate: string | null;
  itemType: string;          // "Auction" | "AuctionWithBuyItNow" | ...
  sellerAlias: string;
  hasBids: boolean;
  bidCount: number;
  /** Current price: highest bid, or the starting price when there are no bids. */
  maxBid: number | null;
  openingBid: number | null;
  nextBid: number | null;
  buyItNowPrice: number | null;
}

export interface TraderaSearchResponse {
  totalNumberOfItems: number;
  totalNumberOfPages: number;
  items: TraderaItem[];
}

function num(v: unknown): number | null {
  return typeof v === "number" && !Number.isNaN(v) ? v : null;
}

/** Map a raw search item to TraderaItem, built from the real API response. */
function normalizeItem(it: any): TraderaItem {
  const url: string = typeof it?.itemUrl === "string" ? it.itemUrl : "";
  return {
    id: Number(it?.id),
    shortDescription: String(it?.shortDescription ?? ""),
    // The API returns http links; prefer https so the browser doesn't block them.
    itemUrl: url.replace(/^http:\/\//i, "https://"),
    thumbnailLink: typeof it?.thumbnailLink === "string" ? it.thumbnailLink : null,
    endDate: typeof it?.endDate === "string" ? it.endDate : null,
    itemType: String(it?.itemType ?? ""),
    sellerAlias: String(it?.sellerAlias ?? ""),
    hasBids: !!it?.hasBids,
    bidCount: Number(it?.bidCount ?? 0),
    maxBid: num(it?.maxBid),
    openingBid: num(it?.openingBid),
    nextBid: num(it?.nextBid),
    buyItNowPrice: num(it?.buyItNowPrice),
  };
}

/**
 * Effective price used for filtering: the lower of the current bid (maxBid) and
 * the Buy-now price where a Buy-now price is present. Falls back to openingBid.
 */
export function effectivePrice(it: TraderaItem): number | null {
  const current = it.maxBid ?? it.openingBid;
  if (it.buyItNowPrice != null && current != null) return Math.min(current, it.buyItNowPrice);
  return it.buyItNowPrice ?? current;
}

/** One page (up to 50 items) of a Tradera search, via the Worker proxy. */
export async function searchTradera(
  query: string,
  categoryId = 0,
  pageNumber = 0,
  orderBy?: string,
): Promise<TraderaSearchResponse> {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();

  const params = new URLSearchParams({
    query,
    categoryId: String(categoryId),
    pageNumber: String(pageNumber),
  });
  if (orderBy) params.set("orderBy", orderBy);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  let res: Response;
  try {
    res = await fetch(`${PROXY}/tradera/search?${params.toString()}`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` },
    });
  } finally {
    clearTimeout(timeout);
  }

  if (res.status === 429) throw new Error("Tradera säger: för många anrop. Försök igen om en stund.");
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Tradera-fel ${res.status}: ${body.slice(0, 160)}`);
  }
  const data = await res.json();
  return {
    totalNumberOfItems: Number(data?.totalNumberOfItems ?? 0),
    totalNumberOfPages: Number(data?.totalNumberOfPages ?? 0),
    items: Array.isArray(data?.items) ? data.items.map(normalizeItem) : [],
  };
}

// ── Watches + seen-state persistence (users/{uid}.traderaJson) ───────────────

export interface Watch {
  id: string;             // local id (uuid-ish)
  query: string;
  minPrice: number | null;
  maxPrice: number | null;
  excludeWords: string[];
  categoryId: number;     // 0 = all categories
  baselined: boolean;     // first run just records what's there; no "new" flags
}

export interface TraderaStore {
  watches: Watch[];
  seen: Record<string, number>; // itemId -> last-seen epoch ms (for pruning)
}

export function emptyStore(): TraderaStore {
  return { watches: [], seen: {} };
}

export async function loadTradera(uid: string): Promise<TraderaStore> {
  try {
    const doc = await db.collection("users").doc(uid).get();
    const json = doc.exists ? (doc.data() as any)?.traderaJson : null;
    if (json) {
      const p = JSON.parse(json);
      return {
        watches: Array.isArray(p?.watches) ? p.watches : [],
        seen: p?.seen && typeof p.seen === "object" ? p.seen : {},
      };
    }
  } catch {
    /* fall through to empty */
  }
  return emptyStore();
}

export async function saveTradera(uid: string, store: TraderaStore): Promise<void> {
  await db.collection("users").doc(uid).set(
    { traderaJson: JSON.stringify(store), updatedAt: new Date().toISOString() },
    { merge: true },
  );
}
